/**
 * API v2 クライアント
 * 新しいデータベーススキーマに対応したAPIクライアント
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
  User, SeekerProfile, CompanyProfile, Resume, Experience, Education, Certification,
  Application, Scout, Message, DashboardStats, ResumeCompletenessCheck,
  LoginRequest, LoginResponse, RegisterUserRequest, RegisterCompanyRequest,
  SearchSeekersRequest, SearchSeekersResponse, CreateResumeRequest, UpdateResumeRequest,
  CreateApplicationRequest, CreateScoutRequest, ApiResponse, ApiError, API_ENDPOINTS,
  ResumeFile,
  CompanyMonthlyPage
} from '@/types/api-v2';

// ============================================================================
// APIクライアント設定
// ============================================================================

class ApiV2Client {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor(baseURL?: string) {
    // Determine effective baseURL for both browser and server.
    // In production we prefer explicit NEXT_PUBLIC_API_URL to avoid relying on Next rewrites.
    const envBase = process.env.NEXT_PUBLIC_API_URL || '';
    const effectiveBaseURL = baseURL
      ? baseURL
      : (typeof window !== 'undefined' ? '' : (envBase || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000')));
    const timeoutMs = Number.parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || '30000', 10);
    this.client = axios.create({
      // In browser we use same-origin (empty baseURL) to avoid CSP/CORS issues.
      baseURL: effectiveBaseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      // クロスサイトでCookieを送らない（セッション認証を使わずDRF Tokenのみ）
      withCredentials: false,
      timeout: Number.isFinite(timeoutMs) ? timeoutMs : 30000,
    });

      // リクエストインターセプター：認証トークンを自動付与
  this.client.interceptors.request.use((config) => {
    const token = this.getToken();
    if (token) {
      // DRFトークン認証に切り替え
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  });

      // レスポンスインターセプター：エラーハンドリングと簡易リトライ
  this.client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;
      
      // 401エラー（認証失敗）でトークンリフレッシュを試行
      if (error.response?.status === 401 && !originalRequest?._retry) {
        originalRequest._retry = true;
        try {
          // DRFトークンは期限がないが、何らかの理由で無効になった場合
          // ログアウトして再ログインを促す
          this.clearToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/auth/login';
          }
        } catch (refreshError) {
          // エラーが発生した場合もログアウト
          this.clearToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/auth/login';
          }
        }
      }
      
      // タイムアウト・ネットワークダウン時のワンリトライ（合計2回）
      if ((error.code === 'ECONNABORTED' || !error.response) && !originalRequest?._retriedOnce) {
        originalRequest._retriedOnce = true;
        // 待機して再試行（Railwayのコールドスタート対策）
        await new Promise((r) => setTimeout(r, 800));
        return this.client.request(originalRequest);
      }
      
      // 追加情報を含めてログ出力（URL/メソッド/ステータス/本文）
      console.error('API Error:', {
        url: (error.config as any)?.url,
        method: (error.config as any)?.method,
        status: error.response?.status,
        data: error.response?.data || error.message,
      });
      return Promise.reject(error);
    }
  );
  }

  // トークン管理
  setToken(drfToken: string, jwtToken?: string) {
    this.token = drfToken; // DRFトークンを優先
    if (typeof window !== 'undefined') {
      localStorage.setItem('drf_token_v2', drfToken);
      localStorage.setItem('auth_token_v2', drfToken); // 後方互換性のため
      if (jwtToken) {
        localStorage.setItem('jwt_token_v2', jwtToken);
      }
    }
  }

  getToken(): string | null {
    if (!this.token && typeof window !== 'undefined') {
      // DRFトークンを優先的に取得
      this.token = localStorage.getItem('drf_token_v2') || localStorage.getItem('auth_token_v2');
    }
    return this.token;
  }

  getRefreshToken(): string | null {
    // DRFトークンは期限がないので、リフレッシュは不要
    // JWTトークンがある場合はそれを返す（将来の拡張用）
    if (typeof window !== 'undefined') {
      return localStorage.getItem('jwt_token_v2');
    }
    return null;
  }

  clearToken() {
    this.token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('drf_token_v2');
      localStorage.removeItem('auth_token_v2');
      localStorage.removeItem('jwt_token_v2');
    }
  }

  // ============================================================================
  // 認証関連API
  // ============================================================================

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>(API_ENDPOINTS.LOGIN, credentials);
    const { drf_token, token, user } = response.data as any;
    // DRFトークンを優先的に使用
    this.setToken(drf_token, token);
    return response.data;
  }

  async registerUser(userData: RegisterUserRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>(API_ENDPOINTS.REGISTER_USER, userData);
    const { drf_token, token } = response.data as any;
    // DRFトークンを優先的に使用
    this.setToken(drf_token, token);
    return response.data;
  }

  async registerCompany(companyData: RegisterCompanyRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>(API_ENDPOINTS.REGISTER_COMPANY, companyData);
    const { drf_token, token } = response.data as any;
    // DRFトークンを優先的に使用
    this.setToken(drf_token, token);
    return response.data;
  }

  async changePassword(payload: { current_password: string; new_password: string; confirm_password?: string }): Promise<{ message: string }> {
    const res = await this.client.post(API_ENDPOINTS.CHANGE_PASSWORD, payload);
    return res.data as any;
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  // ============================================================================
  // プロフィール関連API
  // ============================================================================

  async getUserProfile(): Promise<User> {
    const response = await this.client.get<User>(API_ENDPOINTS.USER_PROFILE);
    return response.data;
  }

  // 公開プロフィール（閲覧用）
  async getPublicUserProfile(userId: string) {
    const url = `${API_ENDPOINTS.PUBLIC_USER_BASE}${userId}/`;
    const res = await this.client.get(url);
    return res.data;
  }

  async getSeekerProfiles(): Promise<SeekerProfile[]> {
    const response = await this.client.get<SeekerProfile[]>(API_ENDPOINTS.SEEKER_PROFILES);
    return response.data;
  }

  async createSeekerProfile(profileData: Partial<SeekerProfile>): Promise<SeekerProfile> {
    // Prefer the upsert-style endpoint for the current user to avoid
    // validation errors when required name fields are missing.
    try {
      const res = await this.client.put<SeekerProfile>('/api/v2/seeker/profile/', profileData);
      return res.data as any;
    } catch (e) {
      // Fallback to legacy create (may require full fields)
      const response = await this.client.post<SeekerProfile>(API_ENDPOINTS.SEEKER_PROFILES, profileData);
      return response.data;
    }
  }

  async updateSeekerProfile(id: string, profileData: Partial<SeekerProfile>): Promise<SeekerProfile> {
    const response = await this.client.patch<SeekerProfile>(`${API_ENDPOINTS.SEEKER_PROFILES}${id}/`, profileData);
    return response.data;
  }

  async updateUserInfo(userId: string, userData: { email?: string; phone?: string; full_name?: string; kana?: string }): Promise<any> {
    const response = await this.client.patch(`/api/v2/users/${userId}/update/`, userData);
    return response.data;
  }

  async updateMyInfo(userData: { email?: string; phone?: string; full_name?: string; kana?: string }): Promise<any> {
    const response = await this.client.patch(`/api/v2/users/me/update/`, userData);
    return response.data;
  }

  async getCompanyProfiles(): Promise<CompanyProfile[]> {
    const response = await this.client.get<CompanyProfile[]>(API_ENDPOINTS.COMPANY_PROFILES);
    return response.data;
  }

  async createCompanyProfile(profileData: Partial<CompanyProfile>): Promise<CompanyProfile> {
    const response = await this.client.post<CompanyProfile>(API_ENDPOINTS.COMPANY_PROFILES, profileData);
    return response.data;
  }

  async updateCompanyProfile(id: string, profileData: Partial<CompanyProfile>): Promise<CompanyProfile> {
    const response = await this.client.patch<CompanyProfile>(`${API_ENDPOINTS.COMPANY_PROFILES}${id}/`, profileData);
    return response.data;
  }

  // ============================================================================
  // 履歴書関連API
  // ============================================================================

  async getResumes(): Promise<Resume[]> {
    const response = await this.client.get<any>(API_ENDPOINTS.RESUMES);
    const data = response.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  async getResume(id: string): Promise<Resume> {
    const response = await this.client.get<Resume>(`${API_ENDPOINTS.RESUMES}${id}/`);
    return response.data;
  }

  // 公開履歴書（閲覧用）
  async getPublicUserResumes(userId: string): Promise<Resume[]> {
    const url = `${API_ENDPOINTS.PUBLIC_USER_BASE}${userId}/resumes/`;
    const res = await this.client.get<Resume[] | { results?: Resume[] }>(url);
    const data = res.data as any;
    return Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  }

  // 企業向け: 求職者の履歴書（公開用、プライバシー配慮済）を取得
  async getCompanyViewUserResumes(userId: string): Promise<Resume[]> {
    const url = `/api/v2/company/users/${userId}/resumes/`;
    const res = await this.client.get<Resume[] | { results?: Resume[] }>(url);
    const data = res.data as any;
    return Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  }

  // 企業向け: 求職者のアップロード履歴書ファイル一覧を取得
  async getCompanyViewUserResumeFiles(userId: string): Promise<any[]> {
    const url = `/api/v2/company/users/${userId}/resume-files/`;
    const res = await this.client.get<any[]>(url);
    return Array.isArray(res.data) ? res.data : [];
  }

  async getPublicUserResumeDetail(userId: string, resumeId: string): Promise<Resume> {
    const url = `${API_ENDPOINTS.PUBLIC_USER_BASE}${userId}/resumes/${resumeId}/`;
    const res = await this.client.get<Resume>(url);
    return res.data as any;
  }

  async createResume(resumeData: CreateResumeRequest): Promise<Resume> {
    const response = await this.client.post<Resume>(API_ENDPOINTS.RESUMES, resumeData);
    return response.data;
  }

  async updateResume(id: string, resumeData: UpdateResumeRequest): Promise<Resume> {
    const response = await this.client.patch<Resume>(`${API_ENDPOINTS.RESUMES}${id}/`, resumeData);
    return response.data;
  }

  // 希望条件だけを更新（サーバ側で安全に正規化）
  async setResumePreferences(id: string, payload: { desired_job?: string; desired_industries?: string[] | string; desired_locations?: string[] | string; }): Promise<Resume> {
    const url = `${API_ENDPOINTS.RESUMES}${id}/set_preferences/`;
    const res = await this.client.patch<Resume>(url, payload);
    return res.data;
  }

  async deleteResume(id: string): Promise<void> {
    await this.client.delete(`${API_ENDPOINTS.RESUMES}${id}/`);
  }

  async checkResumeCompleteness(id: string): Promise<ResumeCompletenessCheck> {
    const response = await this.client.get<ResumeCompletenessCheck>(`${API_ENDPOINTS.RESUMES}${id}/completeness_check/`);
    return response.data;
  }

  // 履歴書ファイル（アップロード）
  async getResumeFiles(): Promise<ResumeFile[]> {
    const res = await this.client.get(API_ENDPOINTS.RESUME_FILES);
    const data = res.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  async uploadResumeFile(file: File, description?: string): Promise<ResumeFile> {
    const form = new FormData();
    form.append('file', file);
    if (description) form.append('description', description);
    const res = await this.client.post(API_ENDPOINTS.RESUME_FILES, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data as any;
  }

  async deleteResumeFile(id: string): Promise<void> {
    await this.client.delete(`${API_ENDPOINTS.RESUME_FILES}${id}/`);
  }

  // 職歴関連
  async addExperience(resumeId: string, experienceData: Partial<Experience>): Promise<Experience> {
    const response = await this.client.post<Experience>(`${API_ENDPOINTS.RESUMES}${resumeId}/add_experience/`, experienceData);
    return response.data;
  }

  async updateExperience(id: string, experienceData: Partial<Experience>): Promise<Experience> {
    const response = await this.client.patch<Experience>(`${API_ENDPOINTS.EXPERIENCES}${id}/`, experienceData);
    return response.data;
  }

  async deleteExperience(id: string): Promise<void> {
    await this.client.delete(`${API_ENDPOINTS.EXPERIENCES}${id}/`);
  }

  // 学歴関連
  async addEducation(resumeId: string, educationData: Partial<Education>): Promise<Education> {
    const response = await this.client.post<Education>(`${API_ENDPOINTS.RESUMES}${resumeId}/add_education/`, educationData);
    return response.data;
  }

  async updateEducation(id: string, educationData: Partial<Education>): Promise<Education> {
    const response = await this.client.patch<Education>(`${API_ENDPOINTS.EDUCATIONS}${id}/`, educationData);
    return response.data;
  }

  async deleteEducation(id: string): Promise<void> {
    await this.client.delete(`${API_ENDPOINTS.EDUCATIONS}${id}/`);
  }

  // 資格関連
  async addCertification(resumeId: string, certificationData: Partial<Certification>): Promise<Certification> {
    const response = await this.client.post<Certification>(`${API_ENDPOINTS.RESUMES}${resumeId}/add_certification/`, certificationData);
    return response.data;
  }

  async updateCertification(id: string, certificationData: Partial<Certification>): Promise<Certification> {
    const response = await this.client.patch<Certification>(`${API_ENDPOINTS.CERTIFICATIONS}${id}/`, certificationData);
    return response.data;
  }

  async deleteCertification(id: string): Promise<void> {
    await this.client.delete(`${API_ENDPOINTS.CERTIFICATIONS}${id}/`);
  }

  // ============================================================================
  // 企業 月次ページ
  // ============================================================================

  async getCompanyMonthlyCurrent(): Promise<CompanyMonthlyPage> {
    const res = await this.client.get<CompanyMonthlyPage>(API_ENDPOINTS.COMPANY_MONTHLY_CURRENT);
    return res.data;
  }

  async getCompanyMonthly(year: number, month: number): Promise<CompanyMonthlyPage> {
    const url = `${API_ENDPOINTS.COMPANY_MONTHLY_BASE}${year}/${month}/`;
    const res = await this.client.get<CompanyMonthlyPage>(url);
    return res.data;
  }

  async updateCompanyMonthly(year: number, month: number, payload: Partial<CompanyMonthlyPage>): Promise<CompanyMonthlyPage> {
    const url = `${API_ENDPOINTS.COMPANY_MONTHLY_BASE}${year}/${month}/`;
    const res = await this.client.put<CompanyMonthlyPage>(url, payload);
    return res.data;
  }

  // ============================================================================
  // 検索・マッチング関連API
  // ============================================================================

  async searchSeekers(searchParams: SearchSeekersRequest): Promise<SearchSeekersResponse> {
    const response = await this.client.get<SearchSeekersResponse>(API_ENDPOINTS.SEARCH_SEEKERS, {
      params: searchParams
    });
    return response.data;
  }

  // 応募関連
  async getApplications(): Promise<Application[]> {
    const response = await this.client.get<any>(API_ENDPOINTS.APPLICATIONS);
    const data = response.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  // 求職者の応募一覧を取得
  async getMyApplications(): Promise<Application[]> {
    const response = await this.client.get<any>('/api/v2/seeker/applications/');
    const data = response.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  async createApplication(applicationData: CreateApplicationRequest): Promise<Application> {
    const response = await this.client.post<Application>(API_ENDPOINTS.APPLICATIONS, applicationData);
    return response.data;
  }

  async updateApplicationStatus(id: string, status: string): Promise<Application> {
    const response = await this.client.patch<Application>(`${API_ENDPOINTS.APPLICATIONS}${id}/`, { status });
    return response.data;
  }

  // 企業向け：案件別応募者一覧
  async getCompanyApplications(jobId?: string, status?: string): Promise<Application[]> {
    const params = new URLSearchParams();
    if (jobId) params.set('job_id', jobId);
    if (status) params.set('status', status);
    const response = await this.client.get<any>(`/api/v2/company/applications/?${params.toString()}`);
    const data = response.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  // 求職者向け：応募一覧（副業フィルタ対応）
  async getMyApplicationsFiltered(type?: 'side_job' | 'fulltime'): Promise<Application[]> {
    const params = type ? `?type=${type}` : '';
    const response = await this.client.get<any>(`/api/v2/seeker/applications/${params}`);
    const data = response.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  // メッセージ関連
  async getSeekerMessages(): Promise<Message[]> {
    const response = await this.client.get<Message[]>(API_ENDPOINTS.MESSAGES_SEEKER);
    return response.data as any;
  }

  // 企業: 新規求人作成（簡易API）
  async createCompanyJob(payload: any): Promise<{ message: string; job_id?: string }>{
    const response = await this.client.post(API_ENDPOINTS.COMPANY_JOBS_NEW, payload);
    return response.data as any;
  }

  async getCompanyJobs(): Promise<import('@/types/api-v2').JobPosting[]> {
    const res = await this.client.get('/api/v2/company/jobs/');
    const data = res.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  // Jobs: public job detail (includes company_name)
  async getJob(jobId: string): Promise<import('@/types/api-v2').JobPosting> {
    const res = await this.client.get(`/api/v2/jobs/${jobId}/`);
    return res.data as any;
  }

  // スカウト関連
  async getScouts(): Promise<Scout[]> {
    const response = await this.client.get<any>(API_ENDPOINTS.SCOUTS);
    const data = response.data as any;
    // DRF Pagination対応：resultsに配列が入る
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  // 企業↔求職者メッセージ
  async getCompanyMessages(userId: string, subject: string = 'company') {
    const res = await this.client.get('/api/v2/company/messages/', { params: { user_id: userId, subject } });
    return res.data as any[];
  }

  async sendCompanyMessage(userId: string, content: string, opts?: { subject?: string; scout_id?: string; application_id?: string }) {
    const body: any = { user_id: userId, content, ...(opts?.subject ? { subject: opts.subject } : {}), ...(opts?.scout_id ? { scout_id: opts.scout_id } : {}), ...(opts?.application_id ? { application_id: opts.application_id } : {}), };
    const res = await this.client.post('/api/v2/company/messages/', body);
    return res.data as any;
  }

  async sendSeekerMessage(companyId: string, content: string, opts?: { subject?: string }) {
    const body: any = { company_id: companyId, content, ...(opts?.subject ? { subject: opts.subject } : {}) };
    const res = await this.client.post('/api/v2/seeker/messages/', body);
    return res.data as any;
  }

  async createScout(scoutData: CreateScoutRequest): Promise<Scout> {
    const response = await this.client.post<Scout>(API_ENDPOINTS.SCOUTS, scoutData);
    return response.data;
  }

  async markScoutViewed(id: string): Promise<Scout> {
    const response = await this.client.post<Scout>(`${API_ENDPOINTS.SCOUTS}${id}/mark_viewed/`);
    return response.data;
  }

  async respondScout(id: string): Promise<Scout> {
    const response = await this.client.post<Scout>(`${API_ENDPOINTS.SCOUTS}${id}/respond/`);
    return response.data;
  }

  async delete(endpoint: string): Promise<void> {
    await this.client.delete(endpoint);
  }

  // ============================================================================
  // ダッシュボード・統計関連API
  // ============================================================================

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.client.get<DashboardStats>(API_ENDPOINTS.DASHBOARD_STATS);
    return response.data;
  }

  // ============================================================================
  // Jobs: Cap / Tickets
  // ============================================================================

  async getJobCapPlan(jobId: string): Promise<import('@/types/api-v2').JobCapPlan> {
    const url = `/api/v2/jobs/${jobId}/cap_plan/`;
    const res = await this.client.get(url);
    return res.data as any;
  }

  // Public jobs
  async getPublicJobs(): Promise<import('@/types/api-v2').JobPosting[]> {
    const res = await this.client.get('/api/v2/jobs/');
    const data = res.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }

  async getPublicJob(id: string): Promise<import('@/types/api-v2').JobPosting> {
    const res = await this.client.get(`/api/v2/jobs/${id}/`);
    return res.data as any;
  }

  // Side jobs (副業・業務委託)
  async getSideJobs(filters?: {
    skills?: string;
    remote_only?: boolean;
    min_hourly_rate?: number;
    max_hours?: number;
    location?: string;
    employment_type?: string;
    sort?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ results: any[]; total: number; page: number; page_size: number; total_pages: number }> {
    const params = new URLSearchParams();
    if (filters?.skills) params.set('skills', filters.skills);
    if (filters?.remote_only) params.set('remote_only', 'true');
    if (filters?.min_hourly_rate) params.set('min_hourly_rate', String(filters.min_hourly_rate));
    if (filters?.max_hours) params.set('max_hours', String(filters.max_hours));
    if (filters?.location) params.set('location', filters.location);
    if (filters?.employment_type) params.set('employment_type', filters.employment_type);
    if (filters?.sort) params.set('sort', filters.sort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.page_size) params.set('page_size', String(filters.page_size));
    const res = await this.client.get(`/api/v2/side-jobs/?${params.toString()}`);
    return res.data as any;
  }

  // Bulk email campaigns (管理者)
  async getBulkEmailCampaigns(params?: { status?: string; page?: number }): Promise<any> {
    const p = new URLSearchParams();
    if (params?.status) p.set('status', params.status);
    if (params?.page) p.set('page', String(params.page));
    const res = await this.client.get(`/api/v2/admin/bulk-email/campaigns/?${p.toString()}`);
    return res.data;
  }

  async createBulkEmailCampaign(data: any): Promise<any> {
    const res = await this.client.post('/api/v2/admin/bulk-email/campaigns/', data);
    return res.data;
  }

  async getBulkEmailCampaign(id: string): Promise<any> {
    const res = await this.client.get(`/api/v2/admin/bulk-email/campaigns/${id}/`);
    return res.data;
  }

  async previewBulkEmailCampaign(id: string): Promise<{ total: number; sample: any[] }> {
    const res = await this.client.post(`/api/v2/admin/bulk-email/campaigns/${id}/preview/`);
    return res.data;
  }

  async sendBulkEmailCampaign(id: string): Promise<any> {
    const res = await this.client.post(`/api/v2/admin/bulk-email/campaigns/${id}/send/`);
    return res.data;
  }

  async cancelBulkEmailCampaign(id: string): Promise<any> {
    const res = await this.client.post(`/api/v2/admin/bulk-email/campaigns/${id}/cancel/`);
    return res.data;
  }

  async getEmailTemplates(): Promise<any[]> {
    const res = await this.client.get('/api/v2/admin/bulk-email/templates/');
    return res.data;
  }

  async createEmailTemplate(data: any): Promise<any> {
    const res = await this.client.post('/api/v2/admin/bulk-email/templates/', data);
    return res.data;
  }

  // Interview slots
  async getInterviewSlots(jobId: string, seekerId?: string): Promise<any[]> {
    const url = `/api/v2/jobs/${jobId}/interview/slots/`;
    const res = await this.client.get(seekerId ? `${url}?seeker=${seekerId}` : url);
    return res.data as any[];
  }

  async proposeInterviewSlots(jobId: string, payload: { seeker: string; slots: { start: string; end: string }[] }): Promise<any[]> {
    const url = `/api/v2/jobs/${jobId}/interview/slots/propose/`;
    const res = await this.client.post(url, payload);
    return res.data as any[];
  }

  async acceptInterviewSlot(jobId: string, slotId: string): Promise<any> {
    const url = `/api/v2/jobs/${jobId}/interview/slots/${slotId}/accept/`;
    const res = await this.client.post(url, {});
    return res.data as any;
  }

  async declineInterviewSlot(jobId: string, slotId: string): Promise<any> {
    const url = `/api/v2/jobs/${jobId}/interview/slots/${slotId}/decline/`;
    const res = await this.client.post(url, {});
    return res.data as any;
  }

  // Seeker: my interview slots (job-agnostic list)
  async getMyInterviewSlots(): Promise<any[]> {
    const url = `/api/v2/seeker/interview/slots/`;
    const res = await this.client.get(url);
    return res.data as any[];
  }

  async setJobCapPlan(jobId: string, payload: { cap_percent: 20 | 22 | 25; cap_amount_limit?: number | null }): Promise<import('@/types/api-v2').JobCapPlan> {
    const url = `/api/v2/jobs/${jobId}/cap_plan/set/`;
    const res = await this.client.post(url, payload);
    return res.data as any;
  }

  async getJobTickets(jobId: string): Promise<import('@/types/api-v2').JobTicketLedger> {
    const url = `/api/v2/jobs/${jobId}/tickets/`;
    const res = await this.client.get(url);
    return res.data as any;
  }

  async issueJobTickets(jobId: string, payload: { tickets_add: number; rollover_allowed?: boolean; as_bonus?: boolean }): Promise<import('@/types/api-v2').JobTicketLedger> {
    const url = `/api/v2/jobs/${jobId}/tickets/issue/`;
    const res = await this.client.post(url, payload);
    return res.data as any;
  }

  async consumeJobTicket(jobId: string, payload: { seeker?: string; seeker_id?: string; scout_id?: string; application_id?: string; interview_date?: string }): Promise<import('@/types/api-v2').JobTicketLedger> {
    const url = `/api/v2/jobs/${jobId}/tickets/consume/`;
    const res = await this.client.post(url, payload);
    return res.data as any;
  }

  // ============================================================================
  // 志望分野特化添削（Career Customization）
  // ============================================================================

  /**
   * 志望分野カスタマイズを作成
   */
  async createCareerCustomization(resumeId: string, data: { target_career: string; target_career_description?: string }): Promise<any> {
    const url = `/api/v2/resumes/${resumeId}/career-customizations/`;
    const res = await this.client.post(url, data);
    return res.data;
  }

  /**
   * 志望分野カスタマイズ一覧を取得
   */
  async getCareerCustomizations(): Promise<any> {
    const url = `/api/v2/career-customizations/`;
    const res = await this.client.get(url);
    return res.data;
  }

  /**
   * 志望分野カスタマイズ詳細を取得
   */
  async getCareerCustomization(customizationId: string): Promise<any> {
    const url = `/api/v2/career-customizations/${customizationId}/`;
    const res = await this.client.get(url);
    return res.data;
  }

  /**
   * カスタマイズの分析結果を取得
   */
  async getCareerCustomizationAnalyses(customizationId: string, minRelevanceScore?: number): Promise<any> {
    const url = `/api/v2/career-customizations/${customizationId}/analyses/`;
    const params = minRelevanceScore ? { min_relevance_score: minRelevanceScore } : {};
    const res = await this.client.get(url, { params });
    return res.data;
  }

  // ==========================================================================
  // アドバイス通知
  // ==========================================================================

  async getAdviceNotifications(): Promise<any> {
    const res = await this.client.get('/api/v2/advice/notifications/');
    return res.data as any;
  }

  async markAdviceRead(subject?: 'resume_advice' | 'advice' | 'interview'): Promise<any> {
    const res = await this.client.post('/api/v2/advice/mark_read/', subject ? { subject } : {});
    return res.data as any;
  }

  // ============================================================================
  // ユーティリティメソッド
  // ============================================================================

  async healthCheck(): Promise<{ status: string }> {
    try {
      const response = await this.client.get('/health/');
      return response.data;
    } catch (error) {
      return { status: 'error' };
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // ファイルアップロード（将来拡張用）
  async uploadFile(file: File, endpoint: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  // 汎用GET/POST/PATCH/PUTメソッド
  async get<T = any>(url: string, config?: any): Promise<{ data: T }> {
    const response = await this.client.get<T>(url, config);
    return response;
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    const response = await this.client.post<T>(url, data, config);
    return response;
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    const response = await this.client.patch<T>(url, data, config);
    return response;
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    const response = await this.client.put<T>(url, data, config);
    return response;
  }

  // ==========================================================================
  // Xrosspoint評価レポート
  // ==========================================================================

  /**
   * 管理者: 求職者の評価を取得
   */
  async getAdminSeekerEvaluation(seekerId: string): Promise<SeekerEvaluation | null> {
    const res = await this.client.get(`/api/v2/admin/seekers/${seekerId}/evaluation/`);
    return res.data;
  }

  /**
   * 管理者: 求職者の評価を作成
   */
  async createAdminSeekerEvaluation(seekerId: string, data: SeekerEvaluationInput): Promise<SeekerEvaluation> {
    const res = await this.client.post(`/api/v2/admin/seekers/${seekerId}/evaluation/`, data);
    return res.data;
  }

  /**
   * 管理者: 求職者の評価を更新
   */
  async updateAdminSeekerEvaluation(seekerId: string, data: Partial<SeekerEvaluationInput>): Promise<SeekerEvaluation> {
    const res = await this.client.put(`/api/v2/admin/seekers/${seekerId}/evaluation/`, data);
    return res.data;
  }

  /**
   * 管理者: 求職者の評価を削除
   */
  async deleteAdminSeekerEvaluation(seekerId: string): Promise<void> {
    await this.client.delete(`/api/v2/admin/seekers/${seekerId}/evaluation/`);
  }

  /**
   * 企業: 求職者の評価を取得（公開されている場合のみ）
   */
  async getCompanySeekerEvaluation(seekerId: string): Promise<SeekerEvaluationCompany | null> {
    const res = await this.client.get(`/api/v2/company/seekers/${seekerId}/evaluation/`);
    return res.data;
  }

  // 求職者本人向け: 自分の評価取得（プランに応じてアドバイスがマスクされる）
  async getMyEvaluation(): Promise<SeekerEvaluationSeeker | null> {
    const res = await this.client.get('/api/v2/seeker/my-evaluation/');
    return res.data;
  }
}

// ============================================================================
// Xrosspoint評価レポートの型定義
// ============================================================================

export interface SeekerEvaluation {
  id: string;
  seeker: string;
  seeker_name: string;
  seeker_email: string;
  verbalization_score: number;
  reproducibility_score: number;
  expertise_score: number;
  average_score: number;
  verbalization_comment: string;
  reproducibility_comment: string;
  expertise_comment: string;
  concerns: string;
  strengths: string;
  overall_comment: string;
  is_visible_to_companies: boolean;
  is_visible_to_seeker: boolean;
  seeker_advice: string;
  created_by: string | null;
  created_by_name: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface SeekerEvaluationInput {
  verbalization_score: number;
  reproducibility_score: number;
  expertise_score: number;
  verbalization_comment?: string;
  reproducibility_comment?: string;
  expertise_comment?: string;
  concerns?: string;
  strengths?: string;
  overall_comment?: string;
  is_visible_to_companies?: boolean;
  is_visible_to_seeker?: boolean;
  seeker_advice?: string;
}

export interface SeekerEvaluationCompany {
  id: string;
  verbalization_score: number;
  reproducibility_score: number;
  expertise_score: number;
  average_score: number;
  verbalization_comment: string;
  reproducibility_comment: string;
  expertise_comment: string;
  concerns: string;
  strengths: string;
  overall_comment: string;
  updated_at: string;
}

// 求職者本人向け評価（プランに応じてアドバイスがマスクされる）
export interface SeekerEvaluationSeeker {
  id: string;
  verbalization_score: number;
  reproducibility_score: number;
  expertise_score: number;
  average_score: number;
  seeker_advice: string;
  is_advice_masked: boolean;
  required_plan: string;
  updated_at: string;
}

// ============================================================================
// エラーハンドリングユーティリティ
// ============================================================================

export const handleApiError = (error: AxiosError): ApiError => {
  if (error.response?.data) {
    return error.response.data as ApiError;
  }
  
  return {
    detail: error.message || 'An unexpected error occurred',
  };
};

export const isApiError = (error: any): error is ApiError => {
  return error && (error.detail || error.errors);
};

// ============================================================================
// React Query用のキー生成関数
// ============================================================================

export const queryKeys = {
  // プロフィール
  userProfile: () => ['user', 'profile'] as const,
  seekerProfiles: () => ['seeker', 'profiles'] as const,
  companyProfiles: () => ['company', 'profiles'] as const,
  
  // 履歴書
  resumes: () => ['resumes'] as const,
  resume: (id: string) => ['resumes', id] as const,
  resumeCompleteness: (id: string) => ['resumes', id, 'completeness'] as const,
  
  // マッチング
  applications: () => ['applications'] as const,
  scouts: () => ['scouts'] as const,
  searchSeekers: (params: SearchSeekersRequest) => ['search', 'seekers', params] as const,
  
  // ダッシュボード
  dashboardStats: () => ['dashboard', 'stats'] as const,

  // 志望分野カスタマイズ
  careerCustomizations: () => ['career', 'customizations'] as const,
  careerCustomization: (id: string) => ['career', 'customizations', id] as const,
} as const;

// ============================================================================
// シングルトンインスタンス
// ============================================================================

export const apiV2Client = new ApiV2Client();
export default apiV2Client;
